// src/components/CalendarGrid/MonthView.jsx
import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './MonthView.module.css';

// Week starts Monday (UK-first). Day names indexed from Mon.
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Build a 6×7 grid of date cells anchored to the given month.
 * weekStartsOn: 0=Sun, 1=Mon (default per spec).
 */
function buildMonthGrid(currentDate, weekStartsOn = 1) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);

  // How many days before the 1st do we need to fill the grid?
  const startDow = firstDayOfMonth.getDay(); // 0=Sun … 6=Sat
  const offset = (startDow - weekStartsOn + 7) % 7;

  const gridStart = new Date(year, month, 1 - offset);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({ date: d, isCurrentMonth: d.getMonth() === month });
  }
  return cells;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getEventsForDay(events, date) {
  if (!events || events.length === 0) return [];
  const dayStart = new Date(date); dayStart.setHours(0, 0, 0, 0);
  const dayEnd   = new Date(date); dayEnd.setHours(23, 59, 59, 999);
  return events.filter(ev => {
    const s = new Date(ev.start);
    const e = new Date(ev.end);
    return s <= dayEnd && e >= dayStart;
  });
}

const MonthView = ({
  currentDate,
  onDateChange,
  events = [],
  onSlotClick,
  onEventClick,
  weekStartsOn = 1,
  theme = 'light',
  hideNav = false,
  // availabilityMap: Record<YYYY-MM-DD, slotCount> — enables availability mode
  // When provided, cells show intensity-based tint instead of event dots.
  availabilityMap,
  // minDate: cells before this date are rendered as past (greyed, not clickable).
  // Defaults to start of today.
  minDate,
}) => {
  const today = useMemo(() => new Date(), []);

  // Start of today (midnight local) used as default minDate
  const effectiveMinDate = useMemo(() => {
    if (minDate) return minDate;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDate]);

  const cells = useMemo(
    () => buildMonthGrid(currentDate, weekStartsOn),
    [currentDate, weekStartsOn]
  );

  const monthLabel = currentDate.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  const goToPrev = () => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    onDateChange(d);
  };

  const goToNext = () => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    onDateChange(d);
  };

  const goToToday = () => onDateChange(new Date());

  const handleDayClick = (cell, isPast) => {
    if (isPast) return;
    if (onSlotClick) onSlotClick(cell.date, null);
  };

  const handleEventClick = (e, event) => {
    e.stopPropagation();
    if (onEventClick) onEventClick(event);
  };

  return (
    <div className={styles.root} data-theme={theme}>
      {/* Navigation bar — hidden when hosted inside CalendarGrid */}
      {!hideNav && (
        <div className={styles.nav}>
          <button
            className={styles.navBtn}
            onClick={goToPrev}
            aria-label="Previous month"
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>

          <div className={styles.navCenter}>
            <span className={styles.monthLabel}>{monthLabel}</span>
            <button className={styles.todayBtn} onClick={goToToday}>
              Today
            </button>
          </div>

          <button
            className={styles.navBtn}
            onClick={goToNext}
            aria-label="Next month"
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* 6×7 grid */}
      <div className={styles.grid} role="grid" aria-label={monthLabel}>
        {/* Day-of-week headers */}
        {DAY_NAMES.map(name => (
          <div key={name} className={styles.dayName} role="columnheader">
            {name}
          </div>
        ))}

        {/* Day cells */}
        {cells.map((cell, i) => {
          const isToday   = isSameDay(cell.date, today);
          const isPast    = cell.date < effectiveMinDate;

          // Availability mode: derive display from availabilityMap
          const slotCount = availabilityMap ? (availabilityMap[toDateKey(cell.date)] || 0) : 0;
          const hasAvailability = availabilityMap && slotCount > 0;
          // Intensity: 1 slot = ~0.17, 6+ slots = 1.0
          const intensity = availabilityMap ? Math.min(slotCount / 6, 1) : 0;

          // Event mode (original): derive from events array
          const dayEvents = availabilityMap ? [] : getEventsForDay(events, cell.date);
          const hasMore   = dayEvents.length > 3;
          const visible   = dayEvents.slice(0, 3);

          const cellLabel = cell.date.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          });

          const cellClasses = [
            styles.cell,
            !cell.isCurrentMonth ? styles.cellOverflow : '',
            isToday && !isPast   ? styles.cellToday   : '',
            isPast               ? styles.cellPast    : '',
            hasAvailability      ? styles.cellAvailable : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={i}
              role="gridcell"
              tabIndex={isPast ? -1 : 0}
              aria-label={cellLabel}
              aria-disabled={isPast ? 'true' : undefined}
              className={cellClasses}
              style={hasAvailability ? { '--avail-intensity': intensity } : undefined}
              onClick={() => handleDayClick(cell, isPast)}
              onKeyDown={e => e.key === 'Enter' && handleDayClick(cell, isPast)}
            >
              <span
                className={[
                  styles.dayNum,
                  isToday && !isPast ? styles.dayNumToday : '',
                ].filter(Boolean).join(' ')}
                aria-current={isToday ? 'date' : undefined}
              >
                {cell.date.getDate()}
              </span>

              {/* Availability mode: slot count badge */}
              {hasAvailability && (
                <span className={styles.availBadge} aria-label={`${slotCount} slots`}>
                  {slotCount}
                </span>
              )}

              {/* Event mode: dots */}
              {dayEvents.length > 0 && (
                <div className={styles.eventList} aria-label="Events">
                  {visible.map(ev => (
                    <button
                      key={ev.id}
                      className={styles.eventDot}
                      style={ev.colour ? { background: ev.colour } : undefined}
                      onClick={e => handleEventClick(e, ev)}
                      title={ev.label || ev.type || 'Event'}
                      aria-label={ev.label || ev.type || 'Event'}
                    />
                  ))}
                  {hasMore && (
                    <span className={styles.eventMore}>
                      +{dayEvents.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthView;
